import * as React from "react"
import * as DialogPrimitive from "@radix-ui/react-dialog"
import { X } from "lucide-react"

import { cn } from "@/lib/utils"

const Dialog = DialogPrimitive.Root

const DialogTrigger = DialogPrimitive.Trigger

const DialogPortal = DialogPrimitive.Portal

const DialogClose = DialogPrimitive.Close

const DialogOverlay = React.forwardRef(({ className, ...props }, ref) => (
  <DialogPrimitive.Overlay
    ref={ref}
    className={cn(
      "fixed inset-0 z-50 bg-black/80  data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0",
      className
    )}
    {...props} />
))
DialogOverlay.displayName = DialogPrimitive.Overlay.displayName

// Phase T2.0A Prompt 1 — Mobile-safe DialogContent.
//
// Changes vs. the vanilla shadcn primitive:
//   • Uses dynamic viewport (100dvh) so iOS Safari URL bar / keyboard cannot
//     push the sticky footer off-screen.
//   • Honours iPhone safe-area insets via env(safe-area-inset-*).
//   • Content is a flex column so callers can wrap the body in a scrollable
//     region and add DialogFooter as a sticky footer (see DialogFooter below).
//   • Full-width on mobile (rounded top/bottom), centred card on ≥sm.
//
// Existing desktop dialogs are unaffected — same max-w-lg, same centred
// positioning, same fade+zoom animation on ≥sm.
const DialogContent = React.forwardRef(({ className, children, ...props }, ref) => (
  <DialogPortal>
    <DialogOverlay />
    <DialogPrimitive.Content
      ref={ref}
      onOpenAutoFocus={(e) => {
        // Phase T2.0A Prompt 1.1 — prevent Radix from auto-focusing the
        // first focusable child (a native <select>), which causes the
        // partner picker to auto-open on mobile Safari. Callers can still
        // move focus manually with onOpenAutoFocus after the fact.
        e.preventDefault();
      }}
      className={cn(
        // Mobile-first: full-width, top-aligned, capped at dynamic viewport.
        "fixed left-0 right-0 top-0 z-50 mx-auto flex w-full flex-col gap-0 border bg-background shadow-lg outline-none",
        "max-h-[100dvh]",
        // Safe-area padding — top applies on mobile (edge-to-edge), bottom
        // padding is added to the DialogFooter separately so it wraps the
        // sticky action bar.
        "pt-[max(env(safe-area-inset-top),12px)]",
        // Desktop: centred card with rounded corners and constrained width.
        "sm:left-[50%] sm:right-auto sm:top-[50%] sm:w-full sm:max-w-lg sm:translate-x-[-50%] sm:translate-y-[-50%] sm:rounded-lg sm:pt-0",
        "sm:max-h-[calc(100dvh-2rem)]",
        // Animations
        "duration-200 data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95",
        className,
      )}
      {...props}>
      {children}
      <DialogPrimitive.Close
        className="absolute right-3 top-3 z-10 rounded-sm opacity-70 ring-offset-background transition-opacity hover:opacity-100 focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 disabled:pointer-events-none data-[state=open]:bg-accent data-[state=open]:text-muted-foreground sm:right-4 sm:top-4"
        style={{
          minHeight: 44, minWidth: 44,
          display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
        }}
        data-testid="dialog-close-button">
        <X className="h-4 w-4" />
        <span className="sr-only">Close</span>
      </DialogPrimitive.Close>
    </DialogPrimitive.Content>
  </DialogPortal>
))
DialogContent.displayName = DialogPrimitive.Content.displayName

// Header — same shape as vanilla shadcn (left-aligned on ≥sm), but callers
// building mobile-safe layouts should place it inside a fixed-height header
// row of the DialogContent flex column and let the body scroll separately.
const DialogHeader = ({
  className,
  ...props
}) => (
  <div
    className={cn("flex flex-col space-y-1.5 px-5 pt-5 sm:px-6 sm:pt-6 text-left", className)}
    {...props} />
)
DialogHeader.displayName = "DialogHeader"

// Body — scrollable middle region. Opt-in for the mobile-safe layout.
//
// IMPORTANT: `min-h-0` is required so that `flex-1` actually shrinks below
// content size inside a bounded flex-column parent (DialogContent). Without
// it, the browser's default `min-height: auto` pushes the body to its full
// content height, which shoves the sticky DialogFooter mid-form on any
// dialog with content taller than the viewport.
const DialogBody = ({ className, ...props }) => (
  <div
    className={cn("min-h-0 flex-1 overflow-y-auto px-5 py-4 sm:px-6", className)}
    {...props} />
)
DialogBody.displayName = "DialogBody"

// Footer — mobile-safe action bar. Sits at the bottom of the DialogContent
// flex column via natural flow (no sticky positioning needed once DialogBody
// has `min-h-0 flex-1 overflow-y-auto`). Includes iPhone home-indicator
// padding by default so the CTA is always reachable on notched devices.
//
// IMPORTANT: We intentionally do NOT use `position: sticky bottom-0` here.
// When DialogContent is `position: fixed` with a nested `overflow-y-auto`
// child, sticky's containing-block resolution becomes browser-defined and
// on desktop Chrome/Safari the footer ends up mid-form. Natural flex-column
// placement puts the footer at the bottom of the bounded DialogContent
// where we want it, without any sticky quirks.
const DialogFooter = ({
  className,
  ...props
}) => (
  <div
    className={cn(
      "z-10 flex flex-col-reverse gap-2 border-t border-border/60 bg-background px-5 py-3 pb-[max(env(safe-area-inset-bottom),12px)] sm:flex-row sm:justify-end sm:space-x-2 sm:px-6 sm:py-4",
      className,
    )}
    {...props} />
)
DialogFooter.displayName = "DialogFooter"

const DialogTitle = React.forwardRef(({ className, ...props }, ref) => (
  <DialogPrimitive.Title
    ref={ref}
    className={cn("text-lg font-semibold leading-none tracking-tight", className)}
    {...props} />
))
DialogTitle.displayName = DialogPrimitive.Title.displayName

const DialogDescription = React.forwardRef(({ className, ...props }, ref) => (
  <DialogPrimitive.Description
    ref={ref}
    className={cn("text-sm text-muted-foreground", className)}
    {...props} />
))
DialogDescription.displayName = DialogPrimitive.Description.displayName

export {
  Dialog,
  DialogPortal,
  DialogOverlay,
  DialogTrigger,
  DialogClose,
  DialogContent,
  DialogHeader,
  DialogBody,
  DialogFooter,
  DialogTitle,
  DialogDescription,
}
